import * as React from "react";
import { NaverMapCanvas } from "./NaverMapCanvas";
import type { Coord } from "./types";

// 지도 클릭/드래그로 한 점(위경도)을 고르는 입력 컴포넌트. NaverMapCanvas를
// "표시"가 아니라 "입력"으로 쓰는 유일한 표면 — 매니저 등록·어르신 가입에서
// 좌표를 직접 찍어 distance 매칭이 실데이터로 동작하게 한다.
//
// 동작:
//   - 지도 클릭 → 그 지점에 마커 1개 드롭 + onChange(lat,lng)
//   - 마커 드래그(dragend) → 미세 조정 후 onChange
//   - "현재 위치" → navigator.geolocation으로 한 번에 지정
//   - value가 외부에서 바뀌면(예: 편집 진입) 마커도 동기화
//
// 주의: NCP 지도 키가 인가되지 않은 도메인(localhost 등)에선 타일이 회색일 수
// 있으나, 클릭→좌표 추출 로직은 타일과 무관하게 동작한다.

const DEFAULT_CENTER: Coord = { lat: 37.5665, lng: 126.978 }; // 서울시청 (좌표/권역 미정 시)

interface Props {
  value: Coord | null;
  onChange: (c: Coord) => void;
  // 초기 지도 중심. value가 없을 때만 사용(예: 선택한 권역의 center). 변경되면
  // (그리고 아직 핀이 없으면) 지도를 그 지점으로 재중심화한다.
  center?: Coord | null;
  zoom?: number;
  height?: number;
}

export function LocationPicker({ value, onChange, center, zoom = 14, height = 220 }: Props) {
  const mapRef = React.useRef<any>(null);
  const markerRef = React.useRef<any>(null);
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const initialCenter = value || center || DEFAULT_CENTER;

  const placeMarker = React.useCallback((c: Coord) => {
    const naver = window.naver;
    const map = mapRef.current;
    if (!naver?.maps || !map) return;
    const pos = new naver.maps.LatLng(c.lat, c.lng);
    if (markerRef.current) {
      markerRef.current.setPosition(pos);
    } else {
      const marker = new naver.maps.Marker({ position: pos, map, draggable: true });
      naver.maps.Event.addListener(marker, "dragend", () => {
        const p = marker.getPosition();
        onChangeRef.current({ lat: p.lat(), lng: p.lng() });
      });
      markerRef.current = marker;
    }
  }, []);

  const handleMapReady = React.useCallback((map: any) => {
    mapRef.current = map;
    const naver = window.naver;
    if (!naver?.maps) return;
    if (value) placeMarker(value);
    naver.maps.Event.addListener(map, "click", (e: any) => {
      const ll = e.coord;
      const c = { lat: ll.lat(), lng: ll.lng() };
      placeMarker(c);
      onChangeRef.current(c);
    });
    // value/center 캡처 갱신은 아래 effect들이 담당. 여기선 1회 초기화만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeMarker]);

  // 외부 value 변경 동기화(편집 진입·GPS 등). 지도 준비 전이면 placeMarker가 no-op.
  React.useEffect(() => {
    if (value) placeMarker(value);
  }, [value, placeMarker]);

  // center(권역) 변경 시 지도 재중심화. 핀(value)이 이미 있어도 권역을 바꾸면
  // 그 권역으로 따라가야 한다(가입 중 권역 재선택). 단 핀 클릭(value 변경)만으로는
  // 재중심화하지 않도록 center가 "실제로" 바뀐 경우에만 동작 — lastCenterRef로 판별.
  // 초기값을 첫 center로 잡아 마운트 시점(편집 진입 등)에는 value 중심을 덮지 않는다.
  const lastCenterRef = React.useRef<Coord | null>(center ?? null);
  React.useEffect(() => {
    const naver = window.naver;
    if (!center || !mapRef.current || !naver?.maps) return;
    const last = lastCenterRef.current;
    if (last && last.lat === center.lat && last.lng === center.lng) return;
    lastCenterRef.current = { lat: center.lat, lng: center.lng };
    mapRef.current.setCenter(new naver.maps.LatLng(center.lat, center.lng));
  }, [center?.lat, center?.lng]);

  const useGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      const naver = window.naver;
      if (mapRef.current && naver?.maps) {
        mapRef.current.setCenter(new naver.maps.LatLng(c.lat, c.lng));
      }
      placeMarker(c);
      onChangeRef.current(c);
    });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span className="muted" style={{ fontSize: 11 }}>
          {value
            ? `좌표: ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}`
            : "지도를 클릭해 위치를 지정하세요"}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={useGps}>
          현재 위치
        </button>
      </div>
      <div style={{ height, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-soft)" }}>
        <NaverMapCanvas center={initialCenter} zoom={zoom} onMapReady={handleMapReady} />
      </div>
    </div>
  );
}
